import { toast } from "react-toastify";
import FinancialReportsSummary from "../../component/FinancialReports/FinancialReportsSummary";
import Hoc from "../../component/HOC";
import { useCallback, useEffect, useState } from "react";
import { exportFinancialReportsData, getFinancialReports } from "../../redux/action/user";
import moment from "moment";
import ScreenLoader from "../../component/ScreenLoader";

const FinancialReports = () => {
    const [loading, setLoading] = useState(false);
    const [reportsData, setReportsData] = useState([]);
    const [filterType, setFilterType] = useState('monthly');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const applyFilter = useCallback((type) => {
        let start = null;
        let end = moment();

        switch (type) {
            case 'today':
                start = moment().startOf('day');
                end = moment().endOf('day');
                break;
            case 'weekly':
                start = moment().startOf('week');
                end = moment().endOf('week');
                break;
            case 'monthly':
                start = moment().startOf('month');
                end = moment().endOf('month');
                break;
            case 'quarterly':
                start = moment().startOf('quarter');
                end = moment().endOf('quarter');
                break;
            case 'yearly':
                start = moment().startOf('year');
                end = moment().endOf('year');
                break;
            case '3years':
                start = moment().subtract(3, 'years').startOf('year');
                break;
            case '5years':
                start = moment().subtract(5, 'years').startOf('year');
                break;
            case '10years':
                start = moment().subtract(10, 'years').startOf('year');
                break;
            case 'lifetime':
                start = null;
                end = null;
                break;
            default:
                break;
        }

        setFilterType(type);
        setStartDate(start);
        setEndDate(end);

        const query = start && end
            ? `?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`
            : '';

        fetchReports(query);
    }, []);

    const fetchReports = useCallback(async (query) => {
        setLoading(true);
        try {
            const res = await getFinancialReports(query);
            const data = res.data?.data;
            setReportsData(data?.userList || []);
        } catch (error) {
            setReportsData([]);
            toast.error("Failed to fetch financial reports.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        applyFilter('monthly');
    }, [applyFilter]);

    const handleExportData = async (exportType = 'excel') => {
        setLoading(true);
        try {
            const query = startDate && endDate
                ? `?startDate=${startDate.format('YYYY-MM-DD')}&endDate=${endDate.format('YYYY-MM-DD')}`
                : '';

            const res = await exportFinancialReportsData(query, exportType);

            const blob = new Blob([res.data], {
                type:
                    exportType === 'pdf'
                        ? 'application/pdf'
                        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download =
                exportType === 'pdf'
                    ? 'financial_reports.pdf'
                    : 'financial_reports.xlsx';

            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            let errorMessage = 'Something went wrong during export.';
            if (
                error.response &&
                error.response.data instanceof Blob &&
                error.response.data.type === 'application/json'
            ) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } catch (parseError) {
                    console.error('Failed to parse blob error:', parseError);
                }
            }

            console.error('Download error:', error);
            toast(errorMessage);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Hoc activeMenu="Financial Reports">
            {loading && <ScreenLoader />}
            <FinancialReportsSummary
                reportsData={reportsData}
                filterType={filterType}
                onFilterChange={applyFilter}
                onExport={handleExportData}
            />
        </Hoc>
    );
};


export default FinancialReports;